import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { getCountryFlag } from "@/lib/countryFlags";
import { useState, useEffect, useRef, useCallback } from "react";
import { ArrowLeft, Check, CheckCircle2, ChevronDown, Clock, Info, Lock, Trash2, PartyPopper, Users, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCountdown } from "@/hooks/useCountdown";
import { analytics } from "@/lib/analytics";

const SEASON_ID = "a07122d7-f1a7-4ce8-a92c-3c7266afebd6";

function SavedIndicator({ show }: { show: boolean }) {
  return (
    <span
      className={cn(
        "text-[10px] text-muted-foreground/60 transition-opacity duration-500",
        show ? "opacity-100" : "opacity-0"
      )}
    >
      Saved
    </span>
  );
}

// ── Narrative parser ──
function MatchupNarrative({ narrative }: { narrative: string }) {
  const dashIndex = narrative.indexOf("—");
  if (dashIndex === -1) {
    return <p className="px-4 pt-3 pb-2 text-xs italic text-muted-foreground">{narrative}</p>;
  }
  const title = narrative.slice(0, dashIndex).trim();
  const rest = narrative.slice(dashIndex + 1).trim();
  return (
    <div className="px-4 pt-3 pb-2 space-y-0.5">
      <p className="text-sm font-bold text-foreground">{title}</p>
      <p className="text-xs italic text-muted-foreground">{rest}</p>
    </div>
  );
}

export default function RaceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [poleDriverOpen, setPoleDriverOpen] = useState(false);
  const [poleMarginInput, setPoleMarginInput] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [savedPole, setSavedPole] = useState(false);
  const [savedMargin, setSavedMargin] = useState(false);
  const [savedMatchups, setSavedMatchups] = useState<Record<string, boolean>>({});
  const [savedSprint, setSavedSprint] = useState<Record<string, boolean>>({});
  const [sprintP1Open, setSprintP1Open] = useState(false);
  const [sprintP2Open, setSprintP2Open] = useState(false);
  const [sprintP3Open, setSprintP3Open] = useState(false);
  // Optimistic picks state
  const [optimisticPicks, setOptimisticPicks] = useState<Record<string, number | null>>({});
  const [showCelebration, setShowCelebration] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userId = user?.id ?? "";

  // ── Queries ──
  const { data: race, isLoading: raceLoading } = useQuery({
    queryKey: ["race", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("races")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: qualiSession } = useQuery({
    queryKey: ["quali-session", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sessions")
        .select("date_start")
        .eq("race_id", id!)
        .eq("session_type", "Qualifying")
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!id,
  });

  const { data: sprintQualiSession } = useQuery({
    queryKey: ["sprint-quali-session", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sessions")
        .select("date_start")
        .eq("race_id", id!)
        .eq("session_type", "Sprint Qualifying")
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!id && !!race?.is_sprint_weekend,
  });

  const { data: drivers } = useQuery({
    queryKey: ["drivers", SEASON_ID],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select("*")
        .eq("season_id", SEASON_ID)
        .order("team_name")
        .order("driver_number");
      if (error) throw error;
      return data;
    },
  });

  const { data: matchups } = useQuery({
    queryKey: ["matchups", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("matchups")
        .select("*")
        .eq("race_id", id!)
        .in("status", ["published", "resolved"])
        .order("display_order");
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: racePick } = useQuery({
    queryKey: ["race-pick", id, userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("race_picks")
        .select("*")
        .eq("race_id", id!)
        .eq("user_id", userId)
        .maybeSingle();
      return data;
    },
    enabled: !!id && !!userId,
  });

  const { data: picks } = useQuery({
    queryKey: ["picks", id, userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("picks")
        .select("*")
        .eq("race_id", id!)
        .eq("user_id", userId);
      return data ?? [];
    },
    enabled: !!id && !!userId,
  });

  // Fetch race session results for positions/DNF
  const { data: raceResults } = useQuery({
    queryKey: ["race-results", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("race_results")
        .select("driver_number, position, dnf, dns, dsq, sessions!inner(race_id, session_type)")
        .eq("sessions.race_id", id!)
        .eq("sessions.session_type", "Race");
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch actual pole sitter from qualifying results
  const { data: actualPoleSitterResult } = useQuery({
    queryKey: ["quali-pole-result", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("race_results")
        .select("driver_number, position, sessions!inner(race_id, session_type)")
        .eq("sessions.race_id", id!)
        .eq("sessions.session_type", "Qualifying")
        .eq("position", 1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Prefill margin input
  useEffect(() => {
    if (racePick?.predicted_pole_margin != null && !poleMarginInput) {
      setPoleMarginInput((racePick.predicted_pole_margin / 1000).toFixed(3));
    }
  }, [racePick]);

  // Clear optimistic state when real data arrives
  useEffect(() => {
    setOptimisticPicks({});
  }, [picks]);

  const qualiCountdown = useCountdown(qualiSession?.date_start ?? null);
  const sprintCountdown = useCountdown(sprintQualiSession?.date_start ?? null);

  const isSprint = race?.is_sprint_weekend ?? false;

  // On sprint weekends, ALL tips lock at Sprint Qualifying time
  const countdown = isSprint ? sprintCountdown : qualiCountdown;
  const isLocked = countdown?.locked ?? false;

  const isResolved = matchups?.some(m => m.status === 'resolved') ?? false;

  const raceResultsMap = new Map<number, { position: number | null; dnf: boolean }>();
  raceResults?.forEach((r) => {
    raceResultsMap.set(r.driver_number, {
      position: r.position,
      dnf: r.dnf || r.dns || r.dsq || false,
    });
  });

  // Lookups resolve against every driver in the season so past rounds still
  // render drivers who have since been stood down (injury, mid-season swap).
  const driverMap = new Map(drivers?.map((d) => [d.driver_number, d]) ?? []);
  // Pickers only ever offer drivers on this weekend's entry list.
  const activeDrivers = drivers?.filter((d) => d.is_active) ?? [];

  const actualPoleSitter = actualPoleSitterResult?.driver_number
    ? driverMap.get(actualPoleSitterResult.driver_number)
    : null;

  // ── Get effective pick for a matchup (optimistic > server) ──
  const getEffectivePick = useCallback((matchupId: string): number | null => {
    if (matchupId in optimisticPicks) return optimisticPicks[matchupId];
    const serverPick = picks?.find((p) => p.matchup_id === matchupId);
    return serverPick?.selected_driver_number ?? null;
  }, [optimisticPicks, picks]);

  // ── Mutations ──
  const upsertRacePick = useMutation({
    mutationFn: async (fields: {
      pole_driver_number?: number | null;
      predicted_pole_margin?: number | null;
      sprint_p1_driver_number?: number | null;
      sprint_p2_driver_number?: number | null;
      sprint_p3_driver_number?: number | null;
    }) => {
      const { error } = await supabase.from("race_picks").upsert(
        { user_id: userId, race_id: id!, ...fields },
        { onConflict: "user_id,race_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["race-pick", id, userId] }),
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const upsertPick = useMutation({
    mutationFn: async (p: { matchup_id: string; selected_driver_number: number }) => {
      const { error } = await supabase.from("picks").upsert(
        { user_id: userId, race_id: id!, ...p },
        { onConflict: "user_id,matchup_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["picks", id, userId] }),
    onError: (e: Error) => {
      // Revert optimistic state on error
      setOptimisticPicks({});
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  const deletePick = useMutation({
    mutationFn: async (matchupId: string) => {
      const { error } = await supabase
        .from("picks")
        .delete()
        .eq("user_id", userId)
        .eq("matchup_id", matchupId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["picks", id, userId] }),
    onError: (e: Error) => {
      setOptimisticPicks({});
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    },
  });

  const clearPolePick = useMutation({
    mutationFn: async () => {
      if (!racePick) return;
      const { error } = await supabase
        .from("race_picks")
        .update({ pole_driver_number: null })
        .eq("user_id", userId)
        .eq("race_id", id!);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["race-pick", id, userId] }),
  });

  const clearMarginPick = useMutation({
    mutationFn: async () => {
      if (!racePick) return;
      const { error } = await supabase
        .from("race_picks")
        .update({ predicted_pole_margin: null })
        .eq("user_id", userId)
        .eq("race_id", id!);
      if (error) throw error;
    },
    onSuccess: () => {
      setPoleMarginInput("");
      queryClient.invalidateQueries({ queryKey: ["race-pick", id, userId] });
    },
  });

  const clearAllTips = useMutation({
    mutationFn: async () => {
      const { error: picksErr } = await supabase
        .from("picks")
        .delete()
        .eq("user_id", userId)
        .eq("race_id", id!);
      if (picksErr) throw picksErr;
      const clearFields: Record<string, null> = {
        pole_driver_number: null,
        predicted_pole_margin: null,
      };
      if (isSprint) {
        clearFields.sprint_p1_driver_number = null;
        clearFields.sprint_p2_driver_number = null;
        clearFields.sprint_p3_driver_number = null;
      }
      const { error: rpErr } = await supabase
        .from("race_picks")
        .update(clearFields)
        .eq("user_id", userId)
        .eq("race_id", id!);
      if (rpErr) throw rpErr;
    },
    onSuccess: () => {
      setPoleMarginInput("");
      setOptimisticPicks({});
      queryClient.invalidateQueries({ queryKey: ["picks", id, userId] });
      queryClient.invalidateQueries({ queryKey: ["race-pick", id, userId] });
      analytics.track("tips_cleared", { race_id: id });
      toast({ title: "All tips cleared" });
    },
    onError: (e: Error) => toast({ title: "Clear failed", description: e.message, variant: "destructive" }),
  });

  // ── Handlers ──
  const handlePoleSelect = (driverNumber: number) => {
    if (isLocked) return;
    if (racePick?.pole_driver_number === driverNumber) {
      setPoleDriverOpen(false);
      clearPolePick.mutate();
      return;
    }
    setPoleDriverOpen(false);
    upsertRacePick.mutate({ pole_driver_number: driverNumber });
    analytics.track("pole_pick", { race_id: id });
    setSavedPole(true);
    setTimeout(() => setSavedPole(false), 1500);
  };

  const handleMarginBlur = () => {
    if (isLocked) return;
    if (poleMarginInput === "") {
      clearMarginPick.mutate();
      return;
    }
    const val = parseFloat(poleMarginInput);
    if (isNaN(val) || val < 0) return;
    const millis = Math.round(val * 1000);
    upsertRacePick.mutate({ predicted_pole_margin: millis });
    analytics.track("pole_margin_set", { race_id: id, margin_ms: millis });
    setSavedMargin(true);
    setTimeout(() => setSavedMargin(false), 1500);
  };

  const handleMarginChange = (v: string) => {
    if (/^\d*\.?\d{0,3}$/.test(v) || v === "") {
      setPoleMarginInput(v);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (v === "") return;
        const val = parseFloat(v);
        if (!isNaN(val) && val >= 0 && !isLocked) {
          const millis = Math.round(val * 1000);
          upsertRacePick.mutate({ predicted_pole_margin: millis });
          setSavedMargin(true);
          setTimeout(() => setSavedMargin(false), 1500);
        }
      }, 1500);
    }
  };

  const handleMatchupPick = (matchupId: string, driverNumber: number) => {
    if (isLocked) return;
    const currentPick = getEffectivePick(matchupId);
    if (currentPick === driverNumber) {
      // Optimistic unselect
      setOptimisticPicks((s) => ({ ...s, [matchupId]: null }));
      deletePick.mutate(matchupId);
      return;
    }
    // Optimistic select
    setOptimisticPicks((s) => ({ ...s, [matchupId]: driverNumber }));
    upsertPick.mutate({ matchup_id: matchupId, selected_driver_number: driverNumber });
    analytics.track("matchup_pick", { race_id: id, matchup_id: matchupId });
    setSavedMatchups((s) => ({ ...s, [matchupId]: true }));
    setTimeout(() => setSavedMatchups((s) => ({ ...s, [matchupId]: false })), 1500);
  };

  // ── Sprint Handlers ──
  const sprintPositions = [
    { key: "sprint_p1_driver_number" as const, label: "Pick 1" },
    { key: "sprint_p2_driver_number" as const, label: "Pick 2" },
    { key: "sprint_p3_driver_number" as const, label: "Pick 3" },
  ];

  const sprintOpenStates = [sprintP1Open, sprintP2Open, sprintP3Open];
  const sprintOpenSetters = [setSprintP1Open, setSprintP2Open, setSprintP3Open];

  const selectedSprintDrivers = isSprint && racePick
    ? [racePick.sprint_p1_driver_number, racePick.sprint_p2_driver_number, racePick.sprint_p3_driver_number]
    : [null, null, null];

  const handleSprintSelect = (posIndex: number, driverNumber: number) => {
    if (isLocked) return;
    const posKey = sprintPositions[posIndex].key;
    const currentVal = racePick?.[posKey] ?? null;
    if (currentVal === driverNumber) {
      sprintOpenSetters[posIndex](false);
      upsertRacePick.mutate({ [posKey]: null });
      return;
    }
    sprintOpenSetters[posIndex](false);
    upsertRacePick.mutate({ [posKey]: driverNumber });
    analytics.track("sprint_pick", { race_id: id, position: posIndex + 1 });
    setSavedSprint((s) => ({ ...s, [posKey]: true }));
    setTimeout(() => setSavedSprint((s) => ({ ...s, [posKey]: false })), 1500);
  };

  // ── Progress (use optimistic picks for matchup count) ──
  const totalMatchups = matchups?.length ?? 0;
  const sprintPicksCount = isSprint ? 3 : 0;
  const totalPicks = totalMatchups + 1 + sprintPicksCount; // matchups + pole (margin is optional tiebreaker)

  // Count optimistic matchup picks
  const optimisticMatchupCount = matchups?.filter((m) => {
    const eff = getEffectivePick(m.id);
    return eff != null;
  }).length ?? 0;

  const sprintCompleted = isSprint
    ? (racePick?.sprint_p1_driver_number ? 1 : 0) +
      (racePick?.sprint_p2_driver_number ? 1 : 0) +
      (racePick?.sprint_p3_driver_number ? 1 : 0)
    : 0;
  const completedPicks =
    (racePick?.pole_driver_number ? 1 : 0) +
    optimisticMatchupCount +
    sprintCompleted;
  const allComplete = completedPicks >= totalPicks && totalPicks > 0;

  // Resolved results counts
  const resolvedCorrect = isResolved
    ? (picks?.filter(p => p.is_correct === true).length ?? 0)
      + (racePick?.pole_is_correct ? 1 : 0)
      + (isSprint ? ((racePick?.sprint_p1_correct ? 1 : 0) + (racePick?.sprint_p2_correct ? 1 : 0) + (racePick?.sprint_p3_correct ? 1 : 0)) : 0)
    : 0;
  const resolvedTotal = (matchups?.length ?? 0) + 1 + (isSprint ? 3 : 0);
  const predictedMarginVal = racePick?.predicted_pole_margin ?? 0;
  const actualMarginVal = racePick?.actual_pole_margin;
  const resolvedMarginDelta = actualMarginVal != null
    ? (Math.abs(predictedMarginVal - actualMarginVal) / 1000).toFixed(3)
    : null;

  // Celebration trigger
  const prevAllComplete = useRef(false);
  useEffect(() => {
    if (allComplete && !prevAllComplete.current) {
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
    }
    prevAllComplete.current = allComplete;
  }, [allComplete]);

  // ── Loading ──
  if (raceLoading) {
    return (
      <div className="px-4 py-6 md:px-8 md:py-10 space-y-4">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (!race) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground">Race not found.</p>
      </div>
    );
  }

  const poleDriver = racePick?.pole_driver_number ? driverMap.get(racePick.pole_driver_number) : null;
  const flag = getCountryFlag(race.country_name);

  // ── Section renderers ──
  const renderQualifyingSection = () => (
    <section className="space-y-4">
      <h2 className="text-lg font-bold">Qualifying</h2>

      {/* Pole */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Pole</h3>
            {isResolved ? (
              racePick?.pole_driver_number ? (
                racePick.pole_is_correct ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )
              ) : (
                <div className="flex items-center gap-1">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <span className="text-xs text-red-500">No pick</span>
                </div>
              )
            ) : (
              <SavedIndicator show={savedPole} />
            )}
          </div>
          <Popover open={!isResolved && poleDriverOpen} onOpenChange={setPoleDriverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                disabled={isLocked || isResolved}
                className="w-full justify-between"
              >
                {poleDriver ? (
                  <span className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: poleDriver.team_colour ? `#${poleDriver.team_colour}` : undefined }}
                    />
                    {poleDriver.full_name}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {isResolved ? "No pick" : "Select a driver…"}
                  </span>
                )}
                {!isResolved && <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
              </Button>
            </PopoverTrigger>
            {!isResolved && (
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search driver…" />
                  <CommandList>
                    <CommandEmpty>No driver found.</CommandEmpty>
                    <CommandGroup>
                      {activeDrivers.map((d) => (
                        <CommandItem
                          key={d.driver_number}
                          value={d.full_name}
                          onSelect={() => handlePoleSelect(d.driver_number)}
                          className="flex items-center gap-2"
                        >
                          <span
                            className="h-3 w-3 rounded-full shrink-0"
                            style={{ backgroundColor: d.team_colour ? `#${d.team_colour}` : undefined }}
                          />
                          <span className="flex-1">
                            {d.full_name}{" "}
                            <span className="text-xs text-muted-foreground">({d.team_name})</span>
                          </span>
                          {racePick?.pole_driver_number === d.driver_number && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            )}
          </Popover>
          {isResolved && actualPoleSitter && (
            <p className="text-xs text-muted-foreground">
              Actual pole: <span className="font-medium text-foreground">{actualPoleSitter.full_name}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pole Margin */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Pole Margin (s)</h3>
            {!isResolved && <SavedIndicator show={savedMargin} />}
          </div>
          {isResolved ? (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Prediction</p>
                <p className="text-sm font-mono font-bold">{(predictedMarginVal / 1000).toFixed(3)}s</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Actual</p>
                <p className="text-sm font-mono font-bold">{actualMarginVal != null ? `${(actualMarginVal / 1000).toFixed(3)}s` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Delta</p>
                <p className="text-sm font-mono font-bold">{resolvedMarginDelta != null ? `${resolvedMarginDelta}s` : "—"}</p>
              </div>
            </div>
          ) : (
            <>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0.234"
                value={poleMarginInput}
                onChange={(e) => handleMarginChange(e.target.value)}
                onBlur={handleMarginBlur}
                disabled={isLocked}
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground">
                What will the gap be between pole and P2? (in seconds)
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );

  const renderSprintSection = () => {
    const sprintHasResults = racePick?.sprint_p1_correct != null;
    const sprintCorrectFields = [racePick?.sprint_p1_correct, racePick?.sprint_p2_correct, racePick?.sprint_p3_correct];

    return (
      <section className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-bold">Sprint Podium</h2>
          <p className="text-xs text-muted-foreground">
            Pick 3 drivers you think will finish on the podium. Order doesn't matter.
          </p>
        </div>


        {sprintPositions.map((pos, posIndex) => {
          const selectedNum = selectedSprintDrivers[posIndex];
          const selectedDriver = selectedNum ? driverMap.get(selectedNum) : null;
          const otherSelected = selectedSprintDrivers
            .filter((_, i) => i !== posIndex)
            .filter(Boolean) as number[];

          const isCorrect = sprintHasResults ? sprintCorrectFields[posIndex] === true : null;

          return (
            <Card key={pos.key}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">{pos.label}</h3>
                  {sprintHasResults ? (
                    selectedNum ? (
                      isCorrect ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )
                    ) : (
                      <div className="flex items-center gap-1">
                        <XCircle className="h-5 w-5 text-red-500" />
                        <span className="text-xs text-red-500">No pick</span>
                      </div>
                    )
                  ) : (
                    <SavedIndicator show={!!savedSprint[pos.key]} />
                  )}
                </div>
                <Popover open={!isResolved && sprintOpenStates[posIndex]} onOpenChange={sprintOpenSetters[posIndex]}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      disabled={isLocked || isResolved}
                      className="w-full justify-between"
                    >
                      {selectedDriver ? (
                        <span className="flex items-center gap-2">
                          <span
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: selectedDriver.team_colour ? `#${selectedDriver.team_colour}` : undefined }}
                          />
                          {selectedDriver.full_name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {isResolved ? "No pick" : "Select a driver…"}
                        </span>
                      )}
                      {!isResolved && <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
                    </Button>
                  </PopoverTrigger>
                  {!isResolved && (
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search driver…" />
                        <CommandList>
                          <CommandEmpty>No driver found.</CommandEmpty>
                          <CommandGroup>
                            {activeDrivers.map((d) => {
                              const isOtherSelected = otherSelected.includes(d.driver_number);
                              return (
                                <CommandItem
                                  key={d.driver_number}
                                  value={d.full_name}
                                  disabled={isOtherSelected}
                                  onSelect={() => {
                                    if (!isOtherSelected) handleSprintSelect(posIndex, d.driver_number);
                                  }}
                                  className={cn(
                                    "flex items-center gap-2",
                                    isOtherSelected && "opacity-40"
                                  )}
                                >
                                  <span
                                    className="h-3 w-3 rounded-full shrink-0"
                                    style={{ backgroundColor: d.team_colour ? `#${d.team_colour}` : undefined }}
                                  />
                                  <span className="flex-1">
                                    {d.full_name}{" "}
                                    <span className="text-xs text-muted-foreground">({d.team_name})</span>
                                  </span>
                                  {selectedNum === d.driver_number && (
                                    <Check className="h-4 w-4 text-primary" />
                                  )}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  )}
                </Popover>
              </CardContent>
            </Card>
          );
        })}
      </section>
    );
  };

  const renderRaceSection = () => (
    <section className="space-y-4">
      <h2 className="text-lg font-bold">Race</h2>

      {matchups?.map((matchup) => {
        const dA = driverMap.get(matchup.driver_a_number);
        const dB = driverMap.get(matchup.driver_b_number);
        const selectedDriver = getEffectivePick(matchup.id);
        const matchupResolved = matchup.status === 'resolved';

        const userPick = picks?.find(p => p.matchup_id === matchup.id);
        const isCorrect = matchupResolved ? (userPick?.is_correct === true) : null;

        const resultA = raceResultsMap.get(matchup.driver_a_number);
        const resultB = raceResultsMap.get(matchup.driver_b_number);

        return (
          <Card key={matchup.id} className="overflow-visible relative">
            <CardContent className="p-0">
              <span className="absolute top-1.5 right-2 z-10">
                {matchupResolved ? (
                  isCorrect ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )
                ) : (
                  <SavedIndicator show={!!savedMatchups[matchup.id]} />
                )}
              </span>
              {matchup.narrative && <MatchupNarrative narrative={matchup.narrative} />}

              <div className="grid grid-cols-2">
                <MatchupDriver
                  driver={dA}
                  isSelected={selectedDriver === matchup.driver_a_number}
                  isLocked={isLocked || isResolved}
                  onSelect={() => handleMatchupPick(matchup.id, matchup.driver_a_number)}
                  side="left"
                  position={matchupResolved ? resultA?.position ?? null : undefined}
                  isDNF={matchupResolved ? resultA?.dnf : undefined}
                  showResults={matchupResolved}
                />
                <MatchupDriver
                  driver={dB}
                  isSelected={selectedDriver === matchup.driver_b_number}
                  isLocked={isLocked || isResolved}
                  onSelect={() => handleMatchupPick(matchup.id, matchup.driver_b_number)}
                  side="right"
                  position={matchupResolved ? resultB?.position ?? null : undefined}
                  isDNF={matchupResolved ? resultB?.dnf : undefined}
                  showResults={matchupResolved}
                />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </section>
  );

  const hasMatchups = matchups && matchups.length > 0;

  return (
    <div className="px-4 py-6 md:px-8 md:py-10 max-w-3xl mx-auto space-y-8 pb-28">
      {/* ── Back Button ── */}
      <button
        onClick={() => navigate("/races")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Races
      </button>

      {/* ── Header ── */}
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="text-xs font-bold">
            Round {race.round_number}
          </Badge>
          {race.is_sprint_weekend && (
            <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
              SPRINT
            </Badge>
          )}
        </div>
        <h1 className="text-2xl font-bold md:text-3xl">{race.meeting_name}</h1>
        <p className="text-muted-foreground">
          {race.country_name} {flag}
        </p>
        <p className="text-sm text-muted-foreground">
          {format(parseISO(race.date_start), "d")} – {format(parseISO(race.date_end), "d MMMM yyyy")}
        </p>

        {isResolved ? (
          <Badge className="mt-2 bg-green-600 hover:bg-green-600 text-white">
            Race Complete
          </Badge>
        ) : countdown ? (
          <div
            className={cn(
              "mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold",
              countdown.locked
                ? "bg-destructive/10 text-destructive"
                : "bg-secondary text-foreground"
            )}
          >
            {countdown.locked ? <Lock className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
            {countdown.text}
          </div>
        ) : null}

        {isSprint && !isResolved && (
          <div className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs bg-blue-500/10 text-blue-400 font-medium">
            <Info className="h-3.5 w-3.5" />
            All tips lock at the start of Sprint Qualifying
          </div>
        )}
      </header>

      {!hasMatchups ? (
        <Card>
          <CardContent className="p-6 text-center space-y-2">
            <p className="text-muted-foreground">
              Matchups haven't been published for this race yet. Check back closer to race week.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Sections ordered by race weekend schedule */}
          {isSprint ? (
            <>
              {renderSprintSection()}
              {renderQualifyingSection()}
              {renderRaceSection()}
            </>
          ) : (
            <>
              {renderQualifyingSection()}
              {renderRaceSection()}
            </>
          )}

          {/* ── Review Dialog ── */}
          <Dialog open={showReview} onOpenChange={setShowReview}>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Review Your Picks</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div>
                  <p className="font-semibold text-muted-foreground">Pole Position</p>
                  <p>{poleDriver?.full_name ?? "—"}</p>
                </div>
                <div>
                  <p className="font-semibold text-muted-foreground">Pole Margin</p>
                  <p>{racePick?.predicted_pole_margin != null ? `${(racePick.predicted_pole_margin / 1000).toFixed(3)}s` : "—"}</p>
                </div>

                {isSprint && (
                  <div className="border-t pt-3">
                    <p className="font-semibold text-muted-foreground mb-2">Sprint Podium</p>
                    {sprintPositions.map((pos, i) => {
                      const num = selectedSprintDrivers[i];
                      const driver = num ? driverMap.get(num) : null;
                      return (
                        <div key={pos.key} className="flex items-center justify-between py-1.5 border-b last:border-0">
                          <span className="text-muted-foreground text-xs">{pos.label}</span>
                          <span className="font-medium">{driver?.full_name ?? "—"}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="border-t pt-3">
                  <p className="font-semibold text-muted-foreground mb-2">Matchup Picks</p>
                  {matchups?.map((m) => {
                    const pickNum = getEffectivePick(m.id);
                    const driver = pickNum ? driverMap.get(pickNum) : null;
                    const dA = driverMap.get(m.driver_a_number);
                    const dB = driverMap.get(m.driver_b_number);
                    return (
                      <div key={m.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                        <span className="text-muted-foreground text-xs">
                          {dA?.name_acronym} vs {dB?.name_acronym}
                        </span>
                        <span className="font-medium">{driver?.full_name ?? "—"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowReview(false)}>
                  Keep Editing
                </Button>
                <Button onClick={() => setShowReview(false)}>Looks Good 👍</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ── Sticky Footer ── */}
          <div className="fixed bottom-16 md:bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm px-4 py-3">
            <div className="max-w-3xl mx-auto">
              {isResolved ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-lg">
                    {resolvedCorrect} / {resolvedTotal} tips
                  </span>
                  <div className="flex items-center gap-3">
                    {resolvedMarginDelta != null && (
                      <span className="text-muted-foreground font-mono">
                        {resolvedMarginDelta}s gap
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/results?race=${id}`)}
                    >
                      <Users className="mr-1.5 h-3.5 w-3.5" />
                      Compare
                    </Button>
                  </div>
                </div>
              ) : isLocked ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Results will be posted after the race
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/results?race=${id}`)}
                  >
                    <Users className="mr-1.5 h-3.5 w-3.5" />
                    Compare
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {allComplete ? (
                        <span className={cn(
                          "font-semibold text-green-500 flex items-center gap-1.5",
                          showCelebration && "animate-scale-in"
                        )}>
                          <PartyPopper className="h-4 w-4" />
                          All tips submitted!
                        </span>
                      ) : (
                        <span className="font-medium">
                          {completedPicks} of {totalPicks} picks
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {completedPicks > 0 && !isLocked && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors">
                              <Trash2 className="h-3 w-3" />
                              Clear All
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Clear all tips?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to clear all tips for this race? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => clearAllTips.mutate()}>
                                Clear All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      {allComplete && (
                        <button
                          className="text-xs text-primary font-medium hover:underline"
                          onClick={() => { analytics.track("tips_review_opened", { race_id: id }); setShowReview(true); }}
                        >
                          Review
                        </button>
                      )}
                    </div>
                  </div>
                  <Progress
                    value={totalPicks > 0 ? (completedPicks / totalPicks) * 100 : 0}
                    className={cn("h-2", allComplete && "[&>div]:bg-green-500")}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Matchup Driver Card ──
function MatchupDriver({
  driver,
  isSelected,
  isLocked,
  onSelect,
  side,
  position,
  isDNF,
  showResults,
}: {
  driver: any;
  isSelected: boolean;
  isLocked: boolean;
  onSelect: () => void;
  side: "left" | "right";
  position?: number | null;
  isDNF?: boolean;
  showResults?: boolean;
}) {
  if (!driver) return <div className="p-4 text-sm text-muted-foreground">Unknown</div>;

  const teamColor = driver.team_colour ? `#${driver.team_colour}` : "hsl(var(--muted))";

  return (
    <button
      type="button"
      disabled={isLocked}
      onClick={onSelect}
      className={cn(
        "flex flex-col items-center gap-1 py-4 px-3 text-center transition-all relative",
        side === "left" ? "border-r" : "",
        !isSelected && !isLocked && "hover:bg-accent/50",
        isLocked && !showResults && "cursor-not-allowed opacity-60",
        isLocked && showResults && "cursor-default"
      )}
      style={
        isSelected
          ? { backgroundColor: `${teamColor}20` }
          : undefined
      }
    >
      {/* Team colour stripe */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: teamColor }}
      />

      <span className="text-lg font-black tracking-wider">{driver.name_acronym}</span>
      <span className="text-xs font-medium truncate max-w-full">{driver.full_name}</span>
      <span className="text-[10px] text-muted-foreground">{driver.team_name}</span>
      {driver.nationality && (
        <span className="text-[10px] text-muted-foreground">{driver.nationality}</span>
      )}

      {showResults && position != null && !isDNF && (
        <span className="mt-1 text-xs font-bold text-muted-foreground">P{position}</span>
      )}
      {showResults && isDNF && (
        <span className="mt-1 text-xs font-bold text-red-500">DNF</span>
      )}

      {isSelected && (
        <div className="absolute top-2 right-2">
          <Check className="h-4 w-4" style={{ color: teamColor }} />
        </div>
      )}
    </button>
  );
}
